import React, { useState, useEffect } from 'react';
import './BackupManagementPage.css';

const BackupManagementPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupSchedule, setBackupSchedule] = useState('0 2 * * *');
  const [cleanupSchedule, setCleanupSchedule] = useState('0 3 * * *');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // טען רשימת גיבויים
  useEffect(() => {
    fetchBackups();
    const interval = setInterval(fetchBackups, 30000); // עדכן כל 30 שניות
    return () => clearInterval(interval);
  }, []);

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/backups/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBackups(data.backups || []);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
    } finally {
      setLoading(false);
    }
  };

  // צור גיבוי ידני
  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/backups/create', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`✅ גיבוי נוצר בהצלחה: ${data.backup.fileName}`);
        fetchBackups();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('שגיאה בעת יצירת גיבוי');
    } finally {
      setLoading(false);
    }
  };

  // שחזור מ-backup
  const handleRestore = async (fileName) => {
    if (!window.confirm(`האם אתה בטוח שברצונך לשחזר מ-${fileName}? הפעולה לא יכולה להיבטל!`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/backups/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`✅ מסד הנתונים שוחזר בהצלחה מ-${fileName}`);
        fetchBackups();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('שגיאה בעת שחזור הגיבוי');
    } finally {
      setLoading(false);
    }
  };

  // ניקה גיבויים ישנים
  const handleCleanup = async () => {
    if (!window.confirm('מחק גיבויים ישנים (יותר מ-30 ימים או יותר מ-10 קבצים)?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/backups/cleanup', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`✅ נמחקו ${data.deletedCount} קבצים ישנים`);
        fetchBackups();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('שגיאה בעת ניקוי גיבויים');
    } finally {
      setLoading(false);
    }
  };

  // דוגמאות לוח זמנים
  const scheduleExamples = [
    { label: 'כל יום בשעה 2 בלילה', value: '0 2 * * *' },
    { label: 'כל 6 שעות', value: '0 */6 * * *' },
    { label: 'כל 12 שעות (2 בלילה ו-14:00)', value: '0 2,14 * * *' },
    { label: 'כל יום ראשון בשעה 3 בלילה', value: '0 3 * * 0' },
    { label: 'כל שעה', value: '0 * * * *' },
    { label: 'תחילת כל חודש', value: '0 0 1 * *' }
  ];

  const parseCron = (cron) => {
    const parts = cron.split(' ');
    if (parts.length !== 5) return 'פורמט לא תקין';
    
    const [min, hour, day, month, weekday] = parts;
    
    if (hour === '*' && min === '0' && day === '*' && month === '*' && weekday === '*') {
      return 'כל שעה';
    }
    if (hour === '2' && min === '0' && day === '*' && month === '*' && weekday === '*') {
      return 'כל יום בשעה 2:00';
    }
    if (hour === '3' && min === '0' && day === '*' && month === '*' && weekday === '*') {
      return 'כל יום בשעה 3:00';
    }
    
    return cron;
  };

  const totalBackups = backups.length;
  const totalSize = backups.reduce((sum, b) => sum + (b.size || 0), 0);
  const latestBackup = backups.length > 0 ? new Date(backups[0].created) : null;

  return (
    <div className="backup-management">
      <h1>⏰ ניהול גיבויים אוטומטיים</h1>

      {error && (
        <div className="alert alert-danger">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="close-btn">✕</button>
        </div>
      )}

      {/* סטטוס כללי */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{totalBackups}</div>
          <div className="stat-label">קבצי גיבוי</div>
          <div className="stat-subtext">{(totalSize / 1024 / 1024).toFixed(2)} MB</div>
        </div>

        <div className="stat-card">
          <div className="stat-number">
            {latestBackup ? latestBackup.toLocaleDateString('he-IL') : '—'}
          </div>
          <div className="stat-label">גיבוי אחרון</div>
          <div className="stat-subtext">
            {latestBackup ? latestBackup.toLocaleTimeString('he-IL') : 'לא בוצע עדיין'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-number">📅</div>
          <div className="stat-label">לוח זמנים</div>
          <div className="stat-subtext">{parseCron(backupSchedule)}</div>
        </div>

        <div className="stat-card">
          <div className="stat-number">🧹</div>
          <div className="stat-label">ניקוי אוטומטי</div>
          <div className="stat-subtext">{parseCron(cleanupSchedule)}</div>
        </div>
      </div>

      {/* כפתורים עיקריים */}
      <div className="action-buttons">
        <button 
          className="btn btn-primary"
          onClick={handleCreateBackup}
          disabled={loading}
        >
          💾 צור גיבוי עכשיו
        </button>

        <button 
          className="btn btn-danger"
          onClick={handleCleanup}
          disabled={loading}
        >
          🗑️ נקה קבצים ישנים
        </button>

        <button 
          className="btn btn-secondary"
          onClick={() => setShowScheduleModal(true)}
        >
          ⚙️ הגדר לוח זמנים
        </button>
      </div>

      {/* טבלת גיבויים */}
      <div className="backups-section">
        <h2>📋 קבצי גיבוי ({totalBackups})</h2>
        
        {loading && backups.length === 0 ? (
          <div className="loading">טוען...</div>
        ) : backups.length === 0 ? (
          <div className="empty-state">
            <p>אין גיבויים עדיין</p>
            <small>אתחל גיבוי בכפתור "צור גיבוי עכשיו"</small>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="backups-table">
              <thead>
                <tr>
                  <th>שם קובץ</th>
                  <th>גודל</th>
                  <th>תאריך</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup, idx) => (
                  <tr key={idx}>
                    <td className="filename">
                      <code>{backup.name}</code>
                    </td>
                    <td>{(backup.size / 1024 / 1024).toFixed(2)} MB</td>
                    <td>
                      {new Date(backup.created).toLocaleDateString('he-IL')}
                      <br />
                      <small>{new Date(backup.created).toLocaleTimeString('he-IL')}</small>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-small btn-restore"
                        onClick={() => handleRestore(backup.name)}
                        disabled={loading}
                        title="שחזר מגיבוי זה"
                      >
                        📥 שחזור
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal לוח זמנים */}
      {showScheduleModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>⚙️ הגדר לוח זמנים</h3>
              <button className="close-btn" onClick={() => setShowScheduleModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>⏰ זמן גיבוי יומי (Cron)</label>
                <input
                  type="text"
                  value={backupSchedule}
                  onChange={(e) => setBackupSchedule(e.target.value)}
                  placeholder="0 2 * * *"
                  className="form-control"
                />
                <small className="help-text">זמן בפורמט Cron (דקה שעה יום חודש יום שבוע)</small>
              </div>

              <div className="schedule-examples">
                <p><strong>דוגמאות:</strong></p>
                {scheduleExamples.slice(0, 3).map((ex, idx) => (
                  <button
                    key={idx}
                    className="example-btn"
                    onClick={() => setBackupSchedule(ex.value)}
                    title={ex.label}
                  >
                    {ex.label}
                  </button>
                ))}
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>🧹 זמן ניקוי אוטומטי (Cron)</label>
                <input
                  type="text"
                  value={cleanupSchedule}
                  onChange={(e) => setCleanupSchedule(e.target.value)}
                  placeholder="0 3 * * *"
                  className="form-control"
                />
                <small className="help-text">זמן לניקוי קבצים ישנים</small>
              </div>

              <div className="cron-help">
                <h4>📖 הסבר Cron:</h4>
                <code>
                  *    *    *    *    *<br/>
                  └─ דקה (0-59)<br/>
                  └─ שעה (0-23)<br/>
                  └─ יום בחודש (1-31)<br/>
                  └─ חודש (1-12)<br/>
                  └─ יום בשבוע (0-6) [0=ראשון]
                </code>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowScheduleModal(false)}
              >
                ביטול
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSuccess('⚠️ זכור: עדכן את ה-.env בשרת עם הזמנים החדשים ותפעל מחדש את תהליך השרת!');
                  setShowScheduleModal(false);
                }}
              >
                💾 שמור הגדרות
              </button>
            </div>
          </div>
        </div>
      )}

      {/* הוראות */}
      <div className="info-section">
        <h3>ℹ️ איך זה עובד?</h3>
        <ul>
          <li><strong>גיבוי אוטומטי:</strong> רץ כל יום בשעה 2 בלילה (ניתן לשינוי)</li>
          <li><strong>ניקוי אוטומטי:</strong> מוחק קבצים ישנים יותר מ-30 ימים או יותר מ-10 קבצים</li>
          <li><strong>בדיקה אוטומטית:</strong> וודא שקובץ הגיבוי תקין לפני ואחרי</li>
          <li><strong>גיבוי ידני:</strong> צור גיבוי בכל זמן בכפתור "צור גיבוי עכשיו"</li>
          <li><strong>שחזור:</strong> בחר קובץ גיבוי וחזור לפי רתצוא</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManagementPage;
